import type { User } from "../models/User.interface";
import { storeToRefs } from "pinia";
import { useAcl } from "vue-simple-acl";
import { useFetchPage } from "~/core/composables/useFetchPage";
import { SearchMode, useSearch } from "~/core/composables/useSearch";
import { useSort } from "~/core/composables/useSort";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import type { UseFetchParameters } from "~/core/models/UseFetchParameters.type";
import type { UseFetchResponse } from "~/core/models/UseFetchResponse.interface";
import {
  Filters,
  type FiltersSelection,
  FiltersType,
  OPERATOR_RSQL_ILIKE,
} from "~/core/models/filters";
import filtersUtils from "~/core/utils/filters.utils";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import type { SelectedItemType } from "~/modules/sector/models/SelectedItem.type";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import userAdministrationService from "~/modules/user/services/userAdministration.service";
import { SectorResource } from "~/types";

const { can } = useAcl();

type UseFetchUserParameters = UseFetchParameters & {
  outSector?: boolean;
};

interface UseFetchUserResponse extends UseFetchResponse<User> {
  itemType: SectorResource;
  getDescription: (item?: SelectedItemType | undefined) => string;
  canManageItems: (sector: Sector) => boolean;
  getItemIds: (items: SelectedItemType[]) => string[];
}

export function useFetchUsers({
  businessUnitCode,
  organizationCode,
  sectorCode,
  outSector,
  searchMode = SearchMode.URL_SEARCH,
  useQueryParametersForPagination,
}: UseFetchUserParameters): UseFetchUserResponse {
  let service = userAdministrationService.getSuperAdministrationUsers.bind(
    userAdministrationService,
  );
  if (organizationCode) {
    if (sectorCode) {
      if (outSector) {
        service =
          userAdministrationService.getOrganizationSectorUnassignedUsers.bind(
            userAdministrationService,
          );
      } else {
        service = userAdministrationService.getOrganizationSectorUsers.bind(
          userAdministrationService,
        );
      }
    } else {
      service = userAdministrationService.getOrganizationUsers.bind(
        userAdministrationService,
      );
    }
  } else if (businessUnitCode) {
    service = userAdministrationService.getBusinessUnitUsers.bind(
      userAdministrationService,
    );
  }

  const appliedFilters: FiltersSelection = new Map([
    [
      Filters.USER_FIRST_OR_LAST_NAME,
      {
        key: ["firstname", "lastname"],
        operator: OPERATOR_RSQL_ILIKE,
        value: "",
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
  ]);

  const { searchParams, filters, setFilter, search, onSearch } = useSearch(
    FiltersType.RSQL,
    appliedFilters,
    searchMode,
  );
  const { sort, onSortChange } = useSort();
  const { isLoading, results, allResults, error, fetchData, fetchAllData } =
    useFetchPage({
      businessUnitCode,
      organizationCode,
      sectorCode,
      service,
      searchParams,
      sort,
      useQueryParametersForPagination,
    });

  const itemType = SectorResource.USER;

  function getDescription(item?: SelectedItemType) {
    return item
      ? `${(item as User)?.firstName} ${(item as User)?.lastName}`
      : "";
  }

  function canManageItems(sector: Sector): boolean {
    const { availableSectors } = storeToRefs(useSectorStore());
    return (
      can([ACLRoles.CAN_READ_SECTOR, ACLRoles.CAN_WRITE_USER]) &&
      availableSectors.value
        .map((availableSector) => availableSector.code)
        .includes(sector.code)
    );
  }

  function getItemIds(items: SelectedItemType[]): string[] {
    return items.map((item) => (item as User).login);
  }

  return {
    isLoading,
    error,
    results,
    allResults,
    filters,
    fetchData,
    fetchAllData,
    setFilter,
    onSortChange,
    search,
    onSearch,
    sort,
    itemType,
    getDescription,
    canManageItems,
    getItemIds,
  };
}
